/**
 * Design systems (docs/plans/2026-10-design-systems.md, round 1): the organisation's look, published
 * as `design-system` catalogue entries that every workbench installs as a skill. A page of its own
 * under Rules beside Customisations rather than a part of it: a design system is set up from files
 * (stylesheets, fonts, logos) and long Markdown, and wants a live preview and its versions, none of
 * which the Customisations page's one-line rows have room for.
 *
 * The publisher: name, version, the tokens and art stylesheets (uploaded, or from the Mojo Up
 * template), fonts with their licences and logos (uploaded as assets), the README, component rules
 * and voice as Markdown, tones, delivery, whether it is the organisation's default, and whether the
 * console wears it. The preview renders a sample heading, card, button and the tones with the
 * stylesheets applied, in a sandboxed frame that may load nothing but what it is given.
 *
 * Round 2: "Upload" takes a folder (zipped here, in the browser) or a zip holding
 * `design-system.json` and opens what it held as a draft to publish; "Download as zip" hands a
 * published system back in the same shape, so it moves between organisations and to a team hub.
 * Each published system says which machines run which version. "Try it on" wears one version's
 * tokens in this browser only, until it reloads.
 *
 * Round 3: each system and each version says where it came from (written here, a zip and what its
 * design-system.json says of its origin, or a starter template) and its fonts' licences; an
 * upload is reviewed (where it came from, every font's licence, what was left out) before it is
 * published, and one whose fonts say no licence is refused with each named; the publisher says
 * what changed in each version, which the version list shows and machines receive with it; on
 * Mojo Up AI Cloud the plan's allowance (design systems, and their files' bytes) is shown beside
 * the other figures.
 *
 * Round 4: each version says how its latest design review went ("12 rules checked, 2 findings"),
 * a review being a workbench agent's check of a page or screen against the system's checklist
 * (DESIGN-REVIEW.md, which the platform writes beside a system's rules and which a draft does not
 * show); "Try it on" wears the art stylesheet and the fonts as well as the tokens; and a folder or a
 * zip dropped on Upload (or on "Upload a folder") opens on the same review as one picked.
 */

import { DESIGN_REVIEW_FILE, DESIGN_SYSTEM_CHANGES_MAX, DESIGN_SYSTEM_PACKAGE_MAX_BYTES, designReviewWords, designSystemNotes, isGeneratedDesignReview, designSystemOriginWords, designSystemProblems, quotaAmountText, writeZip, type CatalogueEntry, type DesignSystemAssetType, type DesignSystemContent, type DesignSystemFont, type DesignSystemImport, type DesignSystemLogo, type DesignSystemNotes, type DesignSystemOrigin, type DesignSystemReview, type DesignSystemReviewSummary, type DesignSystemTone, type DesignSystemUnlicensedFont, type DesignSystemVersion, type DesignSystemVersionNotes } from '@aioe/shared';
import { ArrowLeft, CircleCheck, Display, FolderOpen, Palette, Plus, Xmark } from '@gravity-ui/icons';
import { Dropdown, Label } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';

import { ApiError, designSystemReviewRoutes, designSystemRoutes, fetchAsset, problemText, routes, type DesignSystemSpread } from '../api/client.ts';
import { useCloud } from '../auth/AuthProvider.tsx';
import { allowanceWords, atLimit, licenceWords, provenanceWords, spreadWords } from '../components/designSystemWords.ts';
import { Markdown } from '../components/Markdown.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, SaveBar, Stat, StatusMark, Toggle } from '../components/ui.tsx';
import { FormField } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { Hint } from '../components/ui/Hint.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { ArtHero, ArtSuggestion, VIEW_TONES } from '../components/ui/Art.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { applyConsoleDesignSystem, loadConsoleDesignSystem, subscribeConsoleDesignSystem, tryingOn, wearDesignSystem, withoutImports } from '../orgDesignSystem.ts';

const TONE = VIEW_TONES['design-systems']!;
const PLAIN_ID = 'starter-plain-css';
const TAILWIND_ID = 'starter-tailwind-tokens';
const DELIVERY_TEXT = { offered: 'Offered: people add it', installed: 'Installed on every machine' } as const;

/** What the browser may not say: the type from the file's extension. */
const TYPE_BY_EXTENSION: Record<string, DesignSystemAssetType> = { woff2: 'font/woff2', woff: 'font/woff', ttf: 'font/ttf', otf: 'font/otf', svg: 'image/svg+xml', png: 'image/png', webp: 'image/webp', jpg: 'image/jpeg', jpeg: 'image/jpeg', css: 'text/css' };
const typeOf = (f: File) => TYPE_BY_EXTENSION[f.name.split('.').pop()?.toLowerCase() ?? ''];
const fileSlug = (name: string) => name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'file';

async function toBase64(f: Blob): Promise<string> {
  const bytes = new Uint8Array(await f.arrayBuffer());
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

const content = (e: CatalogueEntry) => e.content as DesignSystemContent;

const megabytes = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

/** A file of a folder, at its path in the folder (`acme/tokens.css`). */
interface FolderFile {
  path: string;
  file: File;
}

/** A picked folder's files at their paths. */
const pickedFolder = (files: File[]): FolderFile[] => files.map((file) => ({ path: file.webkitRelativePath || file.name, file }));

/**
 * A folder the browser picked or had dropped on it, as a zip (stored, no compression: fonts and
 * pictures are compressed already), at the paths the folder had. Dot files are left out.
 */
async function zipFolder(files: FolderFile[]): Promise<Blob> {
  const kept = files.filter((f) => !f.path.split('/').some((seg) => seg.startsWith('.')));
  const total = kept.reduce((n, f) => n + f.file.size, 0);
  if (total > DESIGN_SYSTEM_PACKAGE_MAX_BYTES) throw new Error(`That folder comes to ${megabytes(total)}; a design system is at most ${DESIGN_SYSTEM_PACKAGE_MAX_BYTES / 1024 / 1024} MB.`);
  const entries = await Promise.all(
    kept.map(async (f) => ({
      path: f.path,
      bytes: new Uint8Array(await f.file.arrayBuffer()),
    })),
  );
  return new Blob([writeZip(entries)], { type: 'application/zip' });
}

/** Every file under a dropped folder, at its path from the folder's parent. */
async function walkEntry(entry: FileSystemEntry, out: FolderFile[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
    out.push({ path: entry.fullPath.replace(/^\//, ''), file });
    return;
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries answers in batches until it answers none.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;
    for (const e of batch) await walkEntry(e, out);
  }
}

/** What a drop holds: one zip, or a folder (or loose files) to zip; undefined when it holds neither. */
type Dropped = { kind: 'zip'; file: File } | { kind: 'folder'; name: string; read: () => Promise<FolderFile[]> };

/** Reads a drop. The entries are taken now, while the drop event lasts; the files are read later. */
function droppedOf(dt: DataTransfer): Dropped | undefined {
  const entries = [...dt.items].filter((i) => i.kind === 'file').map((i) => i.webkitGetAsEntry?.() ?? null);
  const files = [...dt.files];
  if (files.length === 1 && entries[0]?.isFile !== false && /\.zip$/i.test(files[0]!.name)) return { kind: 'zip', file: files[0]! };
  const walkable = entries.filter((e): e is FileSystemEntry => Boolean(e));
  if (walkable.length) {
    const folder = walkable.length === 1 && walkable[0]!.isDirectory ? walkable[0]!.name : undefined;
    return {
      kind: 'folder',
      name: folder ?? 'the dropped files',
      read: async () => {
        const out: FolderFile[] = [];
        for (const e of walkable) await walkEntry(e, out);
        return out;
      },
    };
  }
  return files.length
    ? {
        kind: 'folder',
        name: 'the dropped files',
        read: async () => files.map((file) => ({ path: file.name, file })),
      }
    : undefined;
}

/** Whether files are being dragged over this window; while they are, a drop outside the target does nothing (rather than open the file). */
function useFileDrag(): boolean {
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Boolean(e.dataTransfer?.types.includes('Files'));
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth++;
      setDragging(true);
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setDragging(false);
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
      depth = 0;
      setDragging(false);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, []);
  return dragging;
}

/** Where a folder or a zip may be dropped: outlined while files are dragged over the window, filled while over it. */
function DropTarget({ dragging, onDrop, label, className = '', children }: { dragging: boolean; onDrop: (d: Dropped | undefined) => void; label: string; className?: string; children: ReactNode }) {
  const [over, setOver] = useState(false);
  return (
    <div
      role="group"
      aria-label={label}
      className={`transition-colors motion-reduce:transition-none ${dragging ? 'outline-2 outline-offset-4 outline-dashed outline-accent' : ''} ${over ? 'bg-accent/10' : ''} ${className}`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onDrop(droppedOf(e.dataTransfer));
      }}
    >
      {children}
    </div>
  );
}

const counted = (n: number, one: string, many: string) => (n === 0 ? `no ${many}` : n === 1 ? `one ${one}` : `${n} ${many}`);
/** "2 fonts or pictures stored and 3 text files", in words. */
const importedWords = (r: DesignSystemImport) => `${counted(r.assets.length, 'font or picture', 'fonts or pictures')} stored and ${counted(Object.keys(r.entry.files ?? {}).length, 'text file', 'text files')}`;

/** What an import brought, said once above the draft it opened. */
interface Imported {
  from: string;
  result: DesignSystemImport;
}

/** An upload refused because its fonts say no licence: the review lists each one. */
interface Refused {
  from: string;
  message: string;
  fonts: DesignSystemUnlicensedFont[];
}

/** A published version with round 3's notes. */
type VersionRow = DesignSystemVersion & DesignSystemVersionNotes & { review?: DesignSystemReviewSummary };

interface Draft {
  id?: string;
  name: string;
  summary: string;
  version: string;
  useWhen: string;
  tokensPath: string;
  tokensCss?: string;
  artPath: string;
  artCss?: string;
  /** Files beyond the two stylesheets (references, example components), kept as they came. */
  otherFiles: Record<string, string>;
  fonts: DesignSystemFont[];
  logos: DesignSystemLogo[];
  readme: string;
  rules: string;
  voice: string;
  tones: DesignSystemTone[];
  delivery: 'offered' | 'installed';
  useByDefault: boolean;
  consoleUse: boolean;
  rest: Pick<DesignSystemContent, 'library' | 'stylesheets'>;
  /** What changed in this version (round 3), and what the version it was opened at said. */
  changes: string;
  loadedVersion: string;
  loadedChanges: string;
  /** Where it came from; sent when it was imported, or when the version stays the one it was opened at. */
  origin?: DesignSystemOrigin;
  imported?: boolean;
}

const blank = (): Draft => ({ name: '', summary: '', version: '1.0.0', useWhen: '', tokensPath: 'tokens.css', artPath: 'art.css', otherFiles: {}, fonts: [], logos: [], readme: '', rules: '', voice: '', tones: [], delivery: 'offered', useByDefault: false, consoleUse: false, rest: {}, changes: '', loadedVersion: '', loadedChanges: '' });

function draftOf(e: CatalogueEntry, imported = false): Draft {
  const c = content(e);
  const notes = designSystemNotes(e.content);
  const files = { ...(e.files ?? {}) };
  const tokensCss = files[c.tokens];
  const artCss = c.art ? files[c.art] : undefined;
  delete files[c.tokens];
  if (c.art) delete files[c.art];
  // The platform writes DESIGN-REVIEW.md from the rules on every publish: not the publisher's to edit.
  if (isGeneratedDesignReview(files[DESIGN_REVIEW_FILE])) delete files[DESIGN_REVIEW_FILE];
  return {
    id: e.id,
    name: c.name,
    summary: e.summary ?? '',
    version: c.version,
    useWhen: c.useWhen ?? '',
    tokensPath: c.tokens,
    ...(tokensCss !== undefined ? { tokensCss } : {}),
    artPath: c.art ?? 'art.css',
    ...(artCss !== undefined ? { artCss } : {}),
    otherFiles: files,
    fonts: c.fonts ?? [],
    logos: c.logos ?? [],
    readme: c.readme,
    rules: c.rules ?? '',
    voice: c.voice ?? '',
    tones: c.tones ?? [],
    delivery: c.delivery,
    useByDefault: Boolean(c.useByDefault),
    consoleUse: Boolean(c.consoleUse),
    rest: { ...(c.library ? { library: c.library } : {}), ...(c.stylesheets?.length ? { stylesheets: c.stylesheets } : {}) },
    changes: notes.changes ?? '',
    loadedVersion: c.version,
    loadedChanges: notes.changes ?? '',
    ...(notes.origin ? { origin: notes.origin } : {}),
    ...(imported ? { imported } : {}),
  };
}

/**
 * The version field moved: what changed belongs to a version, so a new version starts with none,
 * and going back to the version it was opened at brings its note back.
 */
function withVersion(d: Draft, version: string): Partial<Draft> {
  const same = version.trim() === d.loadedVersion;
  if (same && !d.changes.trim()) return { version, changes: d.loadedChanges };
  if (!same && d.loadedChanges && d.changes === d.loadedChanges) return { version, changes: '' };
  return { version };
}

const slugOf = (name: string) => name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'entry';

function entryOf(d: Draft, taken: Set<string>): CatalogueEntry {
  let id = d.id;
  if (!id) {
    const base = `design-system-${slugOf(d.name)}`;
    id = base;
    for (let i = 2; taken.has(id); i++) id = `${base}-${i}`;
  }
  const sameVersion = d.version.trim() === d.loadedVersion;
  const notes: DesignSystemNotes = {
    // An emptied note on the version it was opened at clears it; a new version without one says nothing.
    ...(d.changes.trim() ? { changes: d.changes.trim() } : d.loadedChanges && sameVersion ? { changes: '' } : {}),
    ...(d.origin && (d.imported || sameVersion) ? { origin: d.origin } : {}),
  };
  const c: DesignSystemContent & DesignSystemNotes = {
    name: d.name.trim(),
    version: d.version.trim(),
    readme: d.readme,
    ...(d.useWhen.trim() ? { useWhen: d.useWhen.trim() } : {}),
    tokens: d.tokensPath,
    ...(d.artCss !== undefined ? { art: d.artPath } : {}),
    ...(d.fonts.length ? { fonts: d.fonts } : {}),
    ...(d.logos.length ? { logos: d.logos } : {}),
    ...(d.rules.trim() ? { rules: d.rules } : {}),
    ...(d.voice.trim() ? { voice: d.voice } : {}),
    ...(d.tones.length ? { tones: d.tones } : {}),
    ...d.rest,
    delivery: d.delivery,
    ...(d.useByDefault ? { useByDefault: true } : {}),
    ...(d.consoleUse ? { consoleUse: true } : {}),
    ...notes,
  };
  return {
    id,
    kind: 'design-system',
    name: d.name.trim(),
    ...(d.summary.trim() ? { summary: d.summary.trim() } : {}),
    version: d.version.trim(),
    content: c,
    files: { ...d.otherFiles, ...(d.tokensCss !== undefined ? { [d.tokensPath]: d.tokensCss } : {}), ...(d.artCss !== undefined ? { [d.artPath]: d.artCss } : {}) },
  };
}

/** One line in plain words: version, how it reaches people, and what it is to the organisation. */
function entryLine(e: CatalogueEntry): string {
  const c = content(e);
  return [`Version ${c.version}`, c.delivery === 'installed' ? 'installed on every machine' : 'offered to people', c.useByDefault ? "the organisation's default" : '', c.consoleUse ? 'the console wears it' : '', `${(c.fonts ?? []).length} ${(c.fonts ?? []).length === 1 ? 'font' : 'fonts'}`, `${(c.tones ?? []).length} ${(c.tones ?? []).length === 1 ? 'tone' : 'tones'}`].filter(Boolean).join(' · ');
}

export function DesignSystemsPage() {
  const cat = useLoad(() => routes.designSystemCatalogue(), []);
  const templates = useLoad(() => routes.designSystemTemplates().catch(() => []), []);
  const [draft, setDraft] = useState<Draft>();
  const [removing, setRemoving] = useState<CatalogueEntry>();
  const [starting, setStarting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<Imported>();
  const [refused, setRefused] = useState<Refused>();
  const [error, setError] = useState<string>();
  const fleet = useLoad(() => routes.customisations().catch(() => undefined), []);
  const spreads = new Map((fleet.data?.designSystems?.systems ?? []).map((x) => [x.id, x]));
  const entries = (cat.data?.entries ?? []).filter((e) => e.kind === 'design-system');
  const byDefault = entries.find((e) => content(e).useByDefault);
  const worn = entries.find((e) => content(e).consoleUse);
  const plain = templates.data?.find((t) => t.id === PLAIN_ID);
  const tailwind = templates.data?.find((t) => t.id === TAILWIND_ID);
  // Every system's versions, with who published each, where it came from and what changed.
  const ids = entries.map((e) => e.id).join(',');
  const versions = useLoad(async () => Object.fromEntries(await Promise.all(entries.map(async (e) => [e.id, await designSystemReviewRoutes.versions(e.id).catch(() => [] as VersionRow[])] as const))), [ids]);
  // Mojo Up AI Cloud: the plan's allowance for design systems and their files.
  const cloud = useCloud();
  const cloudId = cloud?.organisation.id;
  const quotas = useLoad(() => (cloudId ? routes.organisationQuotas(cloudId).catch(() => undefined) : Promise.resolve(undefined)), [cloudId]);
  const plan = quotas.data?.planLabel ?? '';
  const systemsRow = quotas.data?.rows.find((r) => r.key === 'designSystems');
  const bytesRow = quotas.data?.rows.find((r) => r.key === 'assetBytes');

  const startFromTemplate = async (templateId: string) => {
    setStarting(true);
    setError(undefined);
    try {
      const { entry } = await routes.useDesignSystemTemplate(templateId);
      cat.reload();
      quotas.reload();
      setDraft(draftOf(entry));
    } catch (e) {
      setError(problemText(e));
    } finally {
      setStarting(false);
    }
  };

  /** A zip, or a folder zipped here, read by the platform and opened as a draft to publish. */
  const importFrom = async (from: string, name: string, zip: () => Promise<Blob>) => {
    setImporting(true);
    setError(undefined);
    setRefused(undefined);
    try {
      const result = await designSystemRoutes.importNamed(await zip(), name);
      setImported({ from, result });
      setDraft(draftOf(result.entry, true));
    } catch (e) {
      const fonts = e instanceof ApiError ? (e.body as { unlicensedFonts?: DesignSystemUnlicensedFont[] } | undefined)?.unlicensedFonts : undefined;
      if (fonts?.length) setRefused({ from, message: problemText(e), fonts });
      else setError(problemText(e));
    } finally {
      setImporting(false);
    }
  };
  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const dragging = useFileDrag();
  /** A folder or a zip dropped on Upload: the same import, and the same review, as one picked. */
  const dropped = (d: Dropped | undefined) => {
    if (!d) return setError('Drop a folder holding design-system.json, or a zip of one.');
    if (d.kind === 'zip') return void importFrom(d.file.name, d.file.name, async () => d.file);
    void importFrom(d.name === 'the dropped files' ? d.name : `the folder ${d.name}`, `${d.name === 'the dropped files' ? 'dropped' : d.name}/`, async () => zipFolder(await d.read()));
  };
  const closeDraft = (d: Draft | undefined) => {
    if (!d) setImported(undefined);
    setDraft(d);
  };

  const inputs = (
    <>
      <input
        ref={zipInput}
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void importFrom(f.name, f.name, async () => f);
        }}
      />
      <input
        ref={folderInput}
        type="file"
        className="hidden"
        {...{ webkitdirectory: '' }}
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          const folder = files[0]?.webkitRelativePath.split('/')[0] || 'folder';
          if (files.length) void importFrom(`the folder ${folder}`, `${folder}/`, () => zipFolder(pickedFolder(files)));
        }}
      />
    </>
  );

  // Adding or editing one: the form column, on a page of its own until it is published or cancelled.
  if (draft)
    return (
      <PageColumn width="form">
        {inputs}
        <Editor
          draft={draft}
          setDraft={closeDraft}
          imported={imported}
          entries={entries}
          onStartFromTemplate={plain && !draft.id ? (id) => void startFromTemplate(id) : undefined}
          templates={{ plain: PLAIN_ID, tailwind: tailwind ? TAILWIND_ID : undefined }}
          starting={starting}
          onSaved={() => {
            closeDraft(undefined);
            cat.reload();
            versions.reload();
            quotas.reload();
            void loadConsoleDesignSystem();
          }}
        />
      </PageColumn>
    );

  return (
    <PageColumn width="data">
      {inputs}
      <PageHeader
        tone={TONE}
        icon={<Palette />}
        title="Design systems"
        subtitle="The organisation's look: tokens, fonts, logos, rules and voice. Every workbench installs it as a skill, so what agents make looks like yours."
        actions={
          <>
            <DropTarget dragging={dragging} onDrop={dropped} label="Drop a design system's folder or zip here" className="rounded-full">
              <Dropdown>
                <Button size="sm" variant={entries.length ? 'secondary' : 'primary'} isPending={importing}>
                  {dragging ? 'Drop to upload' : 'Upload'}
                </Button>
                <Dropdown.Popover placement="bottom end" className="min-w-56">
                  <Dropdown.Menu aria-label="Upload a design system" onAction={(k) => (k === 'folder' ? folderInput.current?.click() : zipInput.current?.click())}>
                    <Dropdown.Item id="folder" textValue="A folder">
                      <Label>A folder…</Label>
                    </Dropdown.Item>
                    <Dropdown.Item id="zip" textValue="A zip">
                      <Label>A zip…</Label>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown>
            </DropTarget>
            {entries.length > 0 && (
              <Button size="sm" variant="primary" onPress={() => setDraft(blank())}>
                <Plus /> Add a design system
              </Button>
            )}
          </>
        }
      />
      <Explainer id="design-systems" tone={TONE} icon={<Palette />} title="How design systems work" summary="Published once, installed by every workbench as a skill its agents read first">
        <p>A design system is the organisation’s look written down: its tokens (colours, surfaces, radii), its fonts and logos, the rules for building components and how the organisation writes. Every workbench installs it as a skill and reads its README before it makes an interface, a page, a deck or a document.</p>
        <p>Each published version is kept, with what changed and where it came from. One system can be the organisation’s default, which every session is told to load; one can be worn by this console for everyone. Try it on wears a version in this browser only, until it reloads.</p>
      </Explainer>
      <ErrorNote error={cat.error ?? error} />
      <div className="art-stats">
        <Stat tone={TONE} icon={<Palette />} label="Published" value={cat.data ? entries.length : undefined} detail={systemsRow ? `${allowanceWords(systemsRow, plan)}${atLimit(systemsRow) ? ', the most it holds' : ''}` : entries.length ? `${entries.filter((e) => content(e).delivery === 'installed').length} installed on every machine` : 'Nothing yet'} />
        <Stat tone={TONE} icon={<CircleCheck />} label="The default" value={byDefault ? 'Chosen' : 'None'} detail={byDefault ? `${content(byDefault).name}: every session loads it first` : 'Sessions are not told to load one'} />
        <Stat tone={TONE} icon={<Display />} label="The console wears" value={worn ? 'Yours' : 'Its own'} detail={worn ? `${content(worn).name}, version ${content(worn).version}` : "The console's own design"} />
        {bytesRow && <Stat tone={TONE} icon={<FolderOpen />} label="Fonts and pictures" value={bytesRow.used === undefined ? undefined : quotaAmountText('bytes', bytesRow.used)} detail={bytesRow.limit === null ? 'No limit' : `Of ${quotaAmountText('bytes', bytesRow.limit)} on ${plan}${atLimit(bytesRow) ? ', full' : ''}`} />}
      </div>

      {refused && <RefusedReview refused={refused} onAgain={() => folderInput.current?.click()} onClose={() => setRefused(undefined)} />}

      {cat.data && entries.length === 0 ? (
        <ArtHero tone={TONE} icon={<Palette />} title="Give every agent your look" blurb="Publish a design system once; every workbench installs it as a skill and reads its README before it makes an interface, a page, a deck or a document.">
          {/* Two across at most: each card carries a sentence, and a narrower column squeezes it beside the icon. */}
          <div className="grid w-full max-w-3xl gap-2.5 sm:grid-cols-2">
            <ArtSuggestion tone={TONE} icon={<Palette />} disabled={!plain || starting} onPress={() => void startFromTemplate(PLAIN_ID)}>
              <span className="block">Start from the plain CSS starter</span>
              <span className="block text-xs font-normal text-muted">{plain?.note ?? 'Tokens, rules and examples in plain CSS, ready to make your own.'}</span>
            </ArtSuggestion>
            <ArtSuggestion tone={TONE} icon={<Palette />} disabled={!tailwind || starting} onPress={() => void startFromTemplate(TAILWIND_ID)}>
              <span className="block">Start from the Tailwind starter</span>
              <span className="block text-xs font-normal text-muted">{tailwind?.note ?? 'Tokens, rules and examples for Tailwind CSS v4, ready to make your own.'}</span>
            </ArtSuggestion>
            <ArtSuggestion tone={TONE} icon={<Palette />} onPress={() => setDraft(blank())}>
              <span className="block">Start from your own stylesheets</span>
              <span className="block text-xs font-normal text-muted">Upload your tokens and art, add your fonts and logos, and write the README.</span>
            </ArtSuggestion>
            <DropTarget dragging={dragging} onDrop={dropped} label="Drop a design system's folder or zip here" className="grid rounded-xl">
              <ArtSuggestion tone={TONE} icon={<Palette />} disabled={importing} onPress={() => folderInput.current?.click()}>
                <span className="block">Upload a folder</span>
                <span className="block text-xs font-normal text-muted">One holding design-system.json and the files it names, or a zip of one: pick it, or drop it here or on Upload.</span>
              </ArtSuggestion>
            </DropTarget>
          </div>
        </ArtHero>
      ) : null}

      {entries.length > 0 && (
        <ListGroup title="Published" count={entries.length} description="Open one’s menu to edit it, download it as a zip to move it elsewhere, or remove it.">
          {entries.map((e) => (
            <ListRow
              key={e.id}
              icon={<Palette />}
              title={e.name}
              description={
                <>
                  <span className="block">{entryLine(e)}</span>
                  <SourceLine entry={e} versions={versions.data?.[e.id]} />
                  <LatestReviewLine versions={versions.data?.[e.id]} />
                  <MachinesLine spread={spreads.get(e.id)} reporting={fleet.data?.designSystems?.reporting} />
                </>
              }
              compactControl
              control={
                <RowMenu
                  label={`More for ${e.name}`}
                  items={[
                    { id: 'edit', label: 'Edit…' },
                    { id: 'zip', label: 'Download as zip' },
                    { id: 'remove', label: 'Remove…', danger: true },
                  ]}
                  onAction={(id) => (id === 'edit' ? setDraft(draftOf(e)) : id === 'zip' ? void routes.exportDesignSystem(e.id, `${e.id}-${content(e).version}.zip`).catch((x: unknown) => setError(problemText(x))) : setRemoving(e))}
                />
              }
            />
          ))}
        </ListGroup>
      )}

      {entries.length > 0 && <TryItOn entries={entries} versions={versions.data} versionsError={versions.error} />}

      <ConfirmDialog
        isOpen={Boolean(removing)}
        title={`Remove ${removing?.name ?? ''}?`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          try {
            await routes.deleteCatalogue(removing!.id);
            if (content(removing!).consoleUse) applyConsoleDesignSystem(undefined);
            setRemoving(undefined);
            cat.reload();
            quotas.reload();
          } catch (e) {
            setRemoving(undefined);
            setError(problemText(e));
          }
        }}
      >
        Workbenches stop offering it at their next refresh. Its published versions stay on record.
      </ConfirmDialog>
    </PageColumn>
  );
}

/** Which machines run which version, in words; nothing until a machine says. */
function MachinesLine({ spread, reporting }: { spread: DesignSystemSpread | undefined; reporting: number | undefined }) {
  if (!reporting) return null;
  return <span className="block tabular-nums">{spread ? spreadWords(spread) : 'No machine says it has it yet'}</span>;
}

/** Where the current version came from, and its fonts' licences, in one quiet line. */
function SourceLine({ entry, versions }: { entry: CatalogueEntry; versions: VersionRow[] | undefined }) {
  const v = versions?.find((x) => x.current);
  const origin = v?.origin ?? designSystemNotes(entry.content).origin;
  const words = [provenanceWords(origin, v ? (v.publishedByName ?? v.publishedBy) : undefined), licenceWords(content(entry).fonts)].filter(Boolean);
  return words.length ? <span className="block">{words.join(' · ')}</span> : null;
}

/** How the current version's latest design review went, in one quiet line; nothing before the first. */
function LatestReviewLine({ versions }: { versions: VersionRow[] | undefined }) {
  const r = versions?.find((v) => v.current)?.review;
  if (!r) return null;
  return <span className="block tabular-nums">{`Last design review ${timeAgo(r.at)}: ${designReviewWords(r)}`}</span>;
}

/** The review of an upload refused for its fonts: each font without a licence, and what to do. */
function RefusedReview({ refused, onAgain, onClose }: { refused: Refused; onAgain: () => void; onClose: () => void }) {
  return (
    <ListGroup
      title="Not imported"
      description={`${refused.from}: ${refused.message}`}
      actions={
        <>
          <Button size="sm" variant="secondary" onPress={onAgain}>
            Upload again
          </Button>
          <Button size="sm" variant="ghost" onPress={onClose}>
            Close
          </Button>
        </>
      }
    >
      {refused.fonts.map((f) => (
        <ListRow key={f.name} title={f.name} description={<span className="mono">{f.files.join(', ') || 'no files named'}</span>} status={<StatusMark state="error" label="No licence" />} />
      ))}
    </ListGroup>
  );
}

/**
 * Wear one version's tokens on this console, in this browser only and until it reloads, to see how
 * it reads before publishing it or making the console wear it for everyone.
 */
function TryItOn({ entries, versions, versionsError }: { entries: CatalogueEntry[]; versions: Record<string, VersionRow[]> | undefined; versionsError?: string }) {
  const trying = useSyncExternalStore(subscribeConsoleDesignSystem, tryingOn);
  const options: Record<string, string> = {};
  for (const e of entries) {
    const list = versions?.[e.id]?.length ? versions[e.id]! : [{ version: content(e).version, current: true }];
    for (const v of list) options[`${e.id}\n${v.version}`] = `${e.name} ${v.version}${v.current ? ' (current)' : ''}`;
  }
  const [chosen, setChosen] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const selected = chosen ?? (trying ? `${trying.id}\n${trying.version}` : Object.keys(options)[0]);
  const wear = async (key: string | undefined) => {
    setError(undefined);
    if (!key) return void (await wearDesignSystem(undefined));
    const [id, version] = key.split('\n') as [string, string];
    setBusy(true);
    try {
      const current = entries.find((e) => e.id === id);
      await wearDesignSystem(current && content(current).version === version ? current : await routes.designSystemVersion(id, version));
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ListGroup title="Try it on" description="See the console in one version’s tokens, art and fonts before you publish it or have the console wear it. Nobody else sees it, and nothing changes for the organisation.">
      <ListRow
        title="Which version"
        control={
          <ChoiceSelect
            label="Which design system and version"
            className="w-80 max-w-full"
            value={selected}
            options={options}
            onChange={(k) => {
              setChosen(k);
              if (trying) void wear(k);
            }}
          />
        }
      />
      <ListRow
        title="Wear it here"
        description={busy ? 'Fetching its fonts…' : trying ? `Wearing ${trying.name} ${trying.version} in this browser, until it reloads.` : 'In this browser only, until it reloads.'}
        control={<Toggle label="Try it on" checked={Boolean(trying)} isDisabled={busy || !selected} onChange={(on) => void wear(on ? selected : undefined)} />}
      />
      {error ?? versionsError ? (
        <ListBlock>
          <ErrorNote error={error ?? versionsError} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <FormField label={label} hint={hint}>
      {children}
    </FormField>
  );
}

/** A button that opens the file picker. */
function FilePick({ label, accept, multiple, onFiles, isPending }: { label: string; accept: string; multiple?: boolean; onFiles: (files: File[]) => void; isPending?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
      <Button size="sm" variant="secondary" isPending={isPending} onPress={() => input.current?.click()}>
        {label}
      </Button>
    </>
  );
}

/** Markdown, written or read back as it will look. */
function MarkdownField({ label, value, onChange, hint, rows = 8 }: { label: string; value: string; onChange: (v: string) => void; hint?: string; rows?: number }) {
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  return (
    <div className="grid gap-1">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium">{label}</span>
        <Segment aria-label={`${label}: write or preview`} size="sm" selectedKey={mode} onSelectionChange={(k) => setMode(String(k) as 'write' | 'preview')} className="w-fit">
          <Segment.Item id="write">Write</Segment.Item>
          <Segment.Item id="preview">Preview</Segment.Item>
        </Segment>
      </div>
      {mode === 'write' ? (
        <textarea aria-label={label} className={`${inputClass} mono text-xs`} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <div className="max-h-80 overflow-auto rounded-lg border border-separator p-3 text-sm">
          <Markdown text={value} fallback={<span className="text-muted">Nothing written yet.</span>} />
        </div>
      )}
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </div>
  );
}

/** A stylesheet: uploaded as text into the entry's files. */
function Stylesheet({ label, path, css, onCss, onRemove }: { label: string; path: string; css: string | undefined; onCss: (css: string, name: string) => void; onRemove?: () => void }) {
  return (
    <ListRow
      title={label}
      description={css === undefined ? 'None yet' : <span className="mono">{`${path} · ${(new TextEncoder().encode(css).length / 1024).toFixed(1)} KB`}</span>}
      control={
        <>
          <FilePick label={css === undefined ? 'Upload' : 'Replace'} accept=".css,text/css" onFiles={([f]) => void f!.text().then((t) => onCss(t, f!.name))} />
          {css !== undefined && onRemove ? (
            <Button size="sm" variant="ghost" onPress={onRemove}>
              Remove
            </Button>
          ) : null}
        </>
      }
    />
  );
}

/**
 * The review of an upload, above the draft it opened: where it came from, what it says changed,
 * every font with the licence it states, and what was left out. Nothing is published until the
 * administrator publishes it.
 */
function ImportReview({ imported }: { imported: Imported }) {
  const r = imported.result;
  const c = content(r.entry);
  const notes = designSystemNotes(r.entry.content);
  const rows: Array<[string, ReactNode]> = [
    ['Where from', designSystemOriginWords(notes.origin) ?? `Read from ${imported.from}`],
    ['What it holds', `${importedWords(r)}${r.replaces ? '; publishing makes it a new version of the one already published' : ''}`],
    ...(notes.changes ? ([['What changed', notes.changes]] as Array<[string, ReactNode]>) : []),
    [
      'Fonts',
      c.fonts?.length ? (
        <ul className="grid gap-0.5">
          {c.fonts.map((f) => (
            <li key={f.name}>
              {f.name}: <span className="text-muted">{f.licence}</span>
            </li>
          ))}
        </ul>
      ) : (
        'None: the stylesheets name families the machines have'
      ),
    ],
    ...(r.ignored.length ? ([['Left out', `${r.ignored.slice(0, 8).join(', ')}${r.ignored.length > 8 ? ` and ${r.ignored.length - 8} more` : ''}, as design-system.json does not name them`]] as Array<[string, ReactNode]>) : []),
  ];
  return (
    <ListGroup title="Review before you publish" description="Each font is installed on every machine that takes this design system: check its licence allows that before you publish.">
      <ListBlock>
      <dl className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm @[35rem]:grid-cols-[minmax(0,9rem)_minmax(0,1fr)]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted">{k}</dt>
            <dd className="min-w-0 break-words">{v}</dd>
          </div>
        ))}
      </dl>
      </ListBlock>
    </ListGroup>
  );
}

/** Add or edit one design system, with its preview and its versions. */
function Editor({ draft: d, setDraft, entries, imported, onSaved, onStartFromTemplate, templates, starting }: { draft: Draft; setDraft: (d: Draft | undefined) => void; entries: CatalogueEntry[]; imported?: Imported; onSaved: () => void; onStartFromTemplate?: (templateId: string) => void; templates?: { plain: string; tailwind?: string }; starting: boolean }) {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string>();
  const [error, setError] = useState<string>();
  const [fontName, setFontName] = useState('');
  const [fontLicence, setFontLicence] = useState('');
  const set = (patch: Partial<Draft>) => setDraft({ ...d, ...patch });
  const entry = entryOf(d, new Set(entries.map((e) => e.id)));
  const problems = d.readme.trim() && d.tokensCss !== undefined && d.name.trim() ? designSystemProblems(entry).problems : [];
  const missing = !d.name.trim() ? 'Give it a name.' : d.tokensCss === undefined ? 'Upload the tokens stylesheet.' : !d.readme.trim() ? 'Write the README the model reads first.' : !d.version.trim() ? 'Give it a version.' : undefined;
  const published = d.id ? entries.find((e) => e.id === d.id) : undefined;
  const versionTaken = published && content(published).version === d.version.trim();

  const upload = async (files: File[], what: 'font' | 'logo') => {
    setError(undefined);
    setUploading(what);
    try {
      const done: Array<{ id: string; file: File }> = [];
      for (const f of files) {
        const contentType = typeOf(f);
        if (!contentType || (what === 'font' ? !contentType.startsWith('font/') : !contentType.startsWith('image/'))) throw new Error(`${f.name} is not a ${what === 'font' ? 'font (woff2, woff, ttf, otf)' : 'picture (svg, png, webp, jpg)'}`);
        const a = await routes.uploadAsset({ name: f.name, contentType, data: await toBase64(f) });
        done.push({ id: a.id, file: f });
      }
      if (what === 'font') {
        const family = fontName.trim() || done[0]!.file.name.replace(/[-_.].*$/, '');
        set({ fonts: [...d.fonts, { name: family, licence: fontLicence.trim(), files: done.map((x) => ({ asset: x.id, path: `assets/fonts/${fileSlug(x.file.name)}` })) }] });
        setFontName('');
        setFontLicence('');
      } else set({ logos: [...d.logos, ...done.map((x) => ({ asset: x.id, path: `assets/logos/${fileSlug(x.file.name)}`, name: x.file.name.replace(/\.[^.]+$/, '') }))] });
    } catch (e) {
      setError(problemText(e));
    } finally {
      setUploading(undefined);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      // One default and one design system on the console: choosing this one moves the choice here.
      for (const e of entries) {
        if (e.id === entry.id) continue;
        const c = content(e);
        const unDefault = d.useByDefault && c.useByDefault;
        const unConsole = d.consoleUse && c.consoleUse;
        // Through the design-system route, so the other entry keeps what changed and where it came from.
        if (unDefault || unConsole) await designSystemRoutes.publish({ ...e, content: { ...(e.content as object), ...(unDefault ? { useByDefault: false } : {}), ...(unConsole ? { consoleUse: false } : {}) } });
      }
      await designSystemRoutes.publish(entry);
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSaving(false);
    }
  };

  const blocked = Boolean(missing) || problems.length > 0 || d.fonts.some((f) => !f.licence.trim());
  return (
    <>
      <PageHeader
        tone={TONE}
        icon={<Palette />}
        title={d.id ? `Edit ${d.name || 'the design system'}` : 'Add a design system'}
        subtitle={d.id ? 'Publishing makes a new version, or replaces this one when the version stays the same.' : 'Its tokens, fonts, logos, rules and voice. Every workbench installs it as a skill once it is published.'}
        actions={
          <Button size="sm" variant="ghost" onPress={() => setDraft(undefined)}>
            <ArrowLeft className="size-4" />
            Design systems
          </Button>
        }
      />
      {imported && imported.result.entry.id === d.id && <ImportReview imported={imported} />}
      {onStartFromTemplate && (
        <ListGroup>
          <ListRow
            title="Or start from a starter"
            description="Its tokens, rules and examples, to make your own."
            control={
              <>
                <Button size="sm" variant="secondary" isPending={starting} onPress={() => onStartFromTemplate(templates?.plain ?? 'starter-plain-css')}>
                  Plain CSS
                </Button>
                {templates?.tailwind && (
                  <Button size="sm" variant="secondary" isPending={starting} onPress={() => onStartFromTemplate(templates.tailwind!)}>
                    Tailwind
                  </Button>
                )}
              </>
            }
          />
        </ListGroup>
      )}

      <ListGroup title="About it">
        <ListBlock className="grid gap-4">
          <div className="grid items-start gap-3 @[35rem]:grid-cols-[2fr_1fr]">
            <Field label="Name">
              <input className={inputClass} value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="Acme brand" />
            </Field>
            <Field label="Version" hint={versionTaken ? 'Publishing under the same version replaces it; a new version keeps this one readable.' : undefined}>
              <input className={`${inputClass} mono`} value={d.version} onChange={(e) => set(withVersion(d, e.target.value))} placeholder="1.0.0" />
            </Field>
          </div>
          <Field label="What changed in this version" hint="Optional. Shown in the versions and sent to machines with it.">
            <textarea className={inputClass} rows={2} maxLength={DESIGN_SYSTEM_CHANGES_MAX} value={d.changes} onChange={(e) => set({ changes: e.target.value })} placeholder="A darker sea tone; the mark for dark grounds." />
          </Field>
          <Field label="When to use it" hint="One sentence: what the skill says about itself.">
            <input className={inputClass} value={d.useWhen} onChange={(e) => set({ useWhen: e.target.value })} placeholder="Anything Acme makes for customers: interfaces, pages, decks and documents." />
          </Field>
        </ListBlock>
      </ListGroup>

      <ListGroup title="Stylesheets" description="The tokens load first, then the art.">
        <Stylesheet label="Tokens" path={d.tokensPath} css={d.tokensCss} onCss={(tokensCss, name) => set({ tokensCss, ...(d.tokensCss === undefined && !d.id ? { tokensPath: fileSlug(name) } : {}) })} />
        <Stylesheet label="Art (optional)" path={d.artPath} css={d.artCss} onCss={(artCss, name) => set({ artCss, ...(d.artCss === undefined ? { artPath: fileSlug(name) === d.tokensPath ? `art-${fileSlug(name)}` : fileSlug(name) } : {}) })} onRemove={() => set({ artCss: undefined })} />
      </ListGroup>

      <ListGroup title="Fonts" count={d.fonts.length || undefined} description="Each with its licence: a font installed on every machine must be licensed for it.">
        {d.fonts.map((f, i) => (
          <ListRow
            key={`${f.name}-${i}`}
            title={f.name}
            description={<span className="mono">{f.files.map((x) => x.path).join(', ')}</span>}
            control={
              <>
                <input aria-label={`${f.name}'s licence`} className={`${inputClass} w-56 max-w-full`} value={f.licence} placeholder="Licence (OFL-1.1, or in words)" onChange={(e) => set({ fonts: d.fonts.map((x, j) => (j === i ? { ...x, licence: e.target.value } : x)) })} />
                <Button size="sm" variant="ghost" onPress={() => set({ fonts: d.fonts.filter((_, j) => j !== i) })}>
                  Remove
                </Button>
              </>
            }
          />
        ))}
        <ListBlock className="grid gap-2 @[35rem]:grid-cols-[1fr_1fr_auto] @[35rem]:items-end">
          <Field label="Family name">
            <input className={inputClass} value={fontName} onChange={(e) => setFontName(e.target.value)} placeholder="Inter" />
          </Field>
          <Field label="Licence">
            <input className={inputClass} value={fontLicence} onChange={(e) => setFontLicence(e.target.value)} placeholder="OFL-1.1" />
          </Field>
          <div>
            <FilePick label="Upload font files" accept=".woff2,.woff,.ttf,.otf" multiple isPending={uploading === 'font'} onFiles={(fs) => void upload(fs, 'font')} />
          </div>
        </ListBlock>
      </ListGroup>

      <ListGroup
        title="Logos and pictures"
        count={d.logos.length || undefined}
        actions={<FilePick label="Upload logos" accept=".svg,.png,.webp,.jpg,.jpeg" multiple isPending={uploading === 'logo'} onFiles={(fs) => void upload(fs, 'logo')} />}
      >
        {d.logos.length === 0 ? <ListBlock className="text-muted">None yet. Upload SVG, PNG, WebP or JPEG files.</ListBlock> : null}
        {d.logos.map((l, i) => (
          <ListBlock key={l.asset + i} className="grid gap-2 @[35rem]:flex @[35rem]:items-center">
            <input aria-label="Logo name" className={`${inputClass} @[35rem]:w-48`} value={l.name} onChange={(e) => set({ logos: d.logos.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
            <input aria-label={`When to use ${l.name}`} className={`${inputClass} @[35rem]:flex-1`} value={l.use ?? ''} placeholder="When to use it (On dark grounds)" onChange={(e) => set({ logos: d.logos.map((x, j) => (j === i ? { ...x, use: e.target.value || undefined } : x)) })} />
            <Button size="sm" variant="ghost" onPress={() => set({ logos: d.logos.filter((_, j) => j !== i) })}>
              Remove
            </Button>
          </ListBlock>
        ))}
      </ListGroup>

      <ListGroup title="What the model reads" description="The README first, then how components are built and how the organisation writes.">
        <ListBlock>
          <MarkdownField label="README" value={d.readme} onChange={(readme) => set({ readme })} rows={10} />
        </ListBlock>
        <ListBlock>
          <MarkdownField label="Component rules" value={d.rules} onChange={(rules) => set({ rules })} hint="How components are built and laid out." />
        </ListBlock>
        <ListBlock>
          <MarkdownField label="Voice" value={d.voice} onChange={(voice) => set({ voice })} hint="How the organisation writes." />
        </ListBlock>
      </ListGroup>

      <ListGroup title="Tones" count={d.tones.length || undefined} description="A colour each area of a product owns.">
        <ListBlock>
          <Tones tones={d.tones} onChange={(tones) => set({ tones })} />
        </ListBlock>
      </ListGroup>

      <ListGroup title="How it reaches people">
        <ListRow title="Delivery" description="Offered, so people add it themselves, or installed on every machine." control={<ChoiceSelect label="How it reaches people" className="w-64 max-w-full" value={d.delivery} options={DELIVERY_TEXT} onChange={(delivery) => set({ delivery })} />} />
        <ListRow title="The organisation's default" description="Every workbench session is told to load it before making an interface, page, deck, document or picture. Workbench policy can turn the telling off." control={<Toggle label="The organisation's default" checked={d.useByDefault} onChange={(useByDefault) => set({ useByDefault })} />} />
        <ListRow title="The console wears it" description="This console loads its tokens after its own, for everyone in the organisation. Turn it off here to go back to the console's own look." control={<Toggle label="The console wears it" checked={d.consoleUse} onChange={(consoleUse) => set({ consoleUse })} />} />
      </ListGroup>

      <Preview draft={d} />

      {d.id && published && <Versions id={d.id} onOpen={(e) => setDraft({ ...draftOf(e), id: d.id })} />}

      <ErrorNote error={error ?? problems[0]} />
      <SaveBar
        isOpen
        label={missing ?? (d.fonts.some((f) => !f.licence.trim()) ? "Say each font's licence" : d.id ? 'Ready to publish' : 'Not published yet')}
        onDiscard={() => setDraft(undefined)}
        onSave={() => void save()}
        saveLabel="Publish"
        saving={saving}
        isDisabled={blocked}
      />
    </>
  );
}

const safeColour = (c: string) => (c && !/[;{}<>"']/.test(c) ? c : 'transparent');

/** The named tones, each a colour and what it marks. */
function Tones({ tones, onChange }: { tones: DesignSystemTone[]; onChange: (t: DesignSystemTone[]) => void }) {
  const edit = (i: number, patch: Partial<DesignSystemTone>) => onChange(tones.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <div className="grid gap-2">
      {tones.length > 0 && (
        <ul className="grid gap-3 @[40rem]:gap-1">
          {tones.map((t, i) => (
            <li key={i} className="grid grid-cols-[auto_1fr_auto] items-center gap-2 @[40rem]:grid-cols-[auto_8rem_12rem_1fr_auto]">
              <span className="size-5 rounded-full border border-separator" style={{ background: safeColour(t.colour) }} aria-hidden="true" />
              <input aria-label="Tone name" className={inputClass} value={t.name} onChange={(e) => edit(i, { name: e.target.value, id: slugOf(e.target.value).slice(0, 40) })} />
              <input aria-label={`${t.name} colour`} className={`${inputClass} mono col-span-3 @max-[40rem]:order-2 @[40rem]:col-span-1`} value={t.colour} onChange={(e) => edit(i, { colour: e.target.value })} placeholder="oklch(0.68 0.12 180)" />
              <input aria-label={`What ${t.name} marks`} className={`${inputClass} col-span-3 @max-[40rem]:order-2 @[40rem]:col-span-1`} value={t.use ?? ''} onChange={(e) => edit(i, { use: e.target.value || undefined })} placeholder="What it marks" />
              <Hint text={`Remove ${t.name}`}>
                <Button size="sm" variant="ghost" isIconOnly aria-label={`Remove ${t.name}`} className="text-muted" onPress={() => onChange(tones.filter((_, j) => j !== i))}>
                  <Xmark className="size-4" />
                </Button>
              </Hint>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button size="sm" variant="secondary" isDisabled={tones.length >= 16} onPress={() => onChange([...tones, { id: `tone-${tones.length + 1}`, name: `Tone ${tones.length + 1}`, colour: 'oklch(0.65 0.1 180)' }])}>
          Add a tone
        </Button>
      </div>
    </div>
  );
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** A data: address for each asset the preview shows, fetched with the bearer. */
function useAssetData(ids: string[]): Record<string, string> {
  const [data, setData] = useState<Record<string, string>>({});
  const key = [...new Set(ids)].sort().join(',');
  useEffect(() => {
    let live = true;
    void Promise.all(
      key
        .split(',')
        .filter(Boolean)
        .map(async (id) => {
          const blob = await fetchAsset(id);
          return [id, `data:${blob.type || 'application/octet-stream'};base64,${await toBase64(blob)}`] as const;
        }),
    ).then(
      (pairs) => live && setData(Object.fromEntries(pairs)),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [key]);
  return data;
}

/**
 * A sample heading, card, button and the tones with the system's stylesheets applied, in a frame
 * that runs nothing and loads nothing it is not given: fonts and logos arrive as data: addresses,
 * `@import` lines are dropped, and the frame's policy refuses every other address.
 */
function Preview({ draft: d }: { draft: Draft }) {
  const [mode, setMode] = useState<'light' | 'dark'>(() => (document.documentElement.classList.contains('dark') ? 'dark' : 'light'));
  const assets = useAssetData([...d.fonts.flatMap((f) => f.files.map((x) => x.asset)), ...d.logos.slice(0, 1).map((l) => l.asset)]);
  const doc = useMemo(() => {
    const faces = d.fonts.flatMap((f) => f.files.filter((x) => assets[x.asset]).map((x) => `@font-face{font-family:${JSON.stringify(f.name)};src:url(${assets[x.asset]});${x.weight ? `font-weight:${x.weight};` : ''}${x.style ? `font-style:${x.style};` : ''}}`)).join('\n');
    const family = d.fonts[0] ? `${JSON.stringify(d.fonts[0].name)}, ` : '';
    const logo = d.logos[0] && assets[d.logos[0].asset] ? `<img class="logo" src="${assets[d.logos[0].asset]}" alt="">` : '';
    const tones = (d.tones.length ? d.tones : [{ id: 'accent', name: 'Accent', colour: 'var(--accent)' }]).map((t) => `<span class="chip"><span class="dot" style="background:${esc(safeColour(t.colour))}"></span>${esc(t.name)}</span>`).join('');
    const firstTone = d.tones[0]?.id ?? 'teal';
    return `<!doctype html><html class="${mode}" data-theme="mouve-${mode}" style="color-scheme:${mode}"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; font-src data:; img-src data:">
<style>:root{--background:oklch(96% 0.004 180);--foreground:oklch(21% 0.006 180);--surface:oklch(100% 0 0);--muted:oklch(50% 0.01 180);--accent:oklch(59% 0.1 178);--accent-foreground:oklch(99% 0 0);--border:oklch(88% 0.004 180);--separator:oklch(90% 0.004 180)}
.dark{--background:oklch(17% 0.004 180);--foreground:oklch(96% 0.002 180);--surface:oklch(22% 0.004 180);--muted:oklch(70% 0.006 180);--border:oklch(30% 0.004 180);--separator:oklch(28% 0.004 180)}</style>
<style>${faces}</style>
<style>${withoutImports(d.tokensCss ?? '')}</style>
<style>${withoutImports(d.artCss ?? '')}</style>
<style>body{margin:0;padding:20px;background:var(--background);color:var(--foreground);font-family:${family}system-ui,sans-serif;font-size:14px;line-height:1.5}
.top{display:flex;align-items:center;gap:12px;margin-bottom:4px}.logo{height:28px;width:auto}h1{font-size:22px;margin:0;font-weight:600}.lede{color:var(--muted);margin:4px 0 16px}
.card{padding:16px;border-radius:14px;border:1px solid var(--border);background:var(--surface);max-width:420px}.card h2{font-size:15px;margin:0 0 4px;font-weight:600}.card p{margin:0 0 12px;color:var(--muted)}
.btn{display:inline-block;border:0;border-radius:10px;padding:7px 14px;font:inherit;font-weight:500;background:var(--accent);color:var(--accent-foreground)}.btn.second{background:transparent;color:var(--foreground);border:1px solid var(--border);margin-left:6px}
.chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:16px}.chip{display:inline-flex;align-items:center;gap:6px;padding:3px 10px;border-radius:999px;border:1px solid var(--border);font-size:12px}.dot{width:10px;height:10px;border-radius:999px;display:inline-block}</style>
</head><body><div class="top">${logo}<h1>${esc(d.name || 'Your design system')}</h1></div><p class="lede">A heading, a card, a button and the tones, with the stylesheets applied.</p>
<div class="card card-art" data-tone="${esc(firstTone)}"><h2>Quarterly review</h2><p>Twelve projects on track; two wait on a decision.</p><button class="btn" type="button">Open the review</button><button class="btn second" type="button">Later</button></div>
<div class="chips">${tones}</div></body></html>`;
  }, [d.tokensCss, d.artCss, d.fonts, d.logos, d.tones, d.name, assets, mode]);
  return (
    <ListGroup
      title="Preview"
      description="A heading, a card, a button and the tones, with the stylesheets applied."
      actions={
        <Segment aria-label="Preview colour mode" size="sm" selectedKey={mode} onSelectionChange={(k) => setMode(String(k) as 'light' | 'dark')} className="w-fit">
          <Segment.Item id="light">Light</Segment.Item>
          <Segment.Item id="dark">Dark</Segment.Item>
        </Segment>
      }
      empty={d.tokensCss === undefined ? <Empty tone={TONE} icon={<Palette />} compact title="Nothing to preview yet" body="Upload the tokens stylesheet to see a heading, a card, a button and the tones in it." /> : undefined}
    >
      <ListBlock flush>
        <iframe title="Design system preview" sandbox="" srcDoc={doc} className="block h-80 w-full bg-surface" />
      </ListBlock>
    </ListGroup>
  );
}

/** Every published version: the current one, and the earlier ones, each readable and openable as a draft, with what changed and where it came from. */
function Versions({ id, onOpen }: { id: string; onOpen: (e: CatalogueEntry) => void }) {
  const versions = useLoad(() => designSystemReviewRoutes.versions(id), [id]);
  const [error, setError] = useState<string>();
  const list: VersionRow[] = versions.data ?? [];
  if (!list.length) return null;
  return (
    <ListGroup title="Versions" count={list.length}>
      {list.map((v) => {
        const from = provenanceWords(v.origin);
        return (
          <ListRow
            key={v.version}
            title={<span className="mono">{v.version}</span>}
            description={
              <>
                <span className="block">
                  {timeAgo(v.publishedAt)} by {v.publishedByName ?? v.publishedBy}
                </span>
                {v.changes ? <span className="line-clamp-3 whitespace-pre-line break-words text-foreground">{v.changes}</span> : null}
                {from && v.origin?.kind !== 'console' ? <span className="block">{from}</span> : null}
                {v.review ? <VersionReview id={id} version={v.version} review={v.review} /> : null}
              </>
            }
            status={v.current ? <StatusMark state="connected" label="Current" /> : undefined}
            compactControl
            control={
              <RowMenu
                label={`More for version ${v.version}`}
                items={[{ id: 'zip', label: 'Download as zip' }, !v.current && { id: 'open', label: 'Open as a draft' }]}
                onAction={(k) => (k === 'zip' ? void routes.exportDesignSystem(id, `${id}-${v.version}.zip`, v.version).catch((e: unknown) => setError(problemText(e))) : void routes.designSystemVersion(id, v.version).then(onOpen, (e) => setError(problemText(e))))}
              />
            }
          />
        );
      })}
      {error ?? versions.error ? (
        <ListBlock>
          <ErrorNote error={error ?? versions.error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/**
 * A version's latest design review in words ("Review of the quarterly report, 2 days ago: 12 rules
 * checked, 2 findings"), and its findings on asking: each the rule it names, what is wrong and where.
 */
function VersionReview({ id, version, review }: { id: string; version: string; review: DesignSystemReviewSummary }) {
  const [full, setFull] = useState<DesignSystemReview>();
  const [rules, setRules] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const words = `${review.title ? `Review of ${review.title}` : 'Design review'}, ${timeAgo(review.at)}: ${designReviewWords(review)}`;
  if (!review.findings) return <div className="text-xs text-muted tabular-nums">{words}</div>;
  return (
    <details
      className="text-xs"
      onToggle={(e) => {
        if (!(e.currentTarget as HTMLDetailsElement).open || full) return;
        Promise.all([designSystemReviewRoutes.reviews(id, version, 1), designSystemReviewRoutes.checklist(id, version).catch(() => undefined)]).then(
          ([r, list]) => {
            setRules(Object.fromEntries((list?.rules ?? []).map((x) => [x.id, x.text])));
            setFull(r.reviews.find((x) => x.id === review.id) ?? r.reviews[0]);
          },
          (x: unknown) => setError(problemText(x)),
        );
      }}
    >
      <summary className="cursor-[var(--cursor-interactive)] text-muted tabular-nums">{words}</summary>
      <ul className="mt-1.5 grid gap-1.5 border-l-2 border-separator pl-3">
        {full?.findings.map((f, i) => (
          <li key={i} className="grid gap-0.5">
            <span>
              <span className="font-medium">{rules[f.rule] ?? f.rule}</span>: {f.note}
            </span>
            {f.where || f.severity === 'must' ? <span className="text-muted">{[f.severity === 'must' ? 'Breaks the system' : '', f.where ? `Where: ${f.where}` : ''].filter(Boolean).join(' · ')}</span> : null}
          </li>
        ))}
        {!full && !error ? <li className="text-muted">Reading the findings…</li> : null}
      </ul>
      <ErrorNote error={error} />
    </details>
  );
}
